import { useCallback, useEffect, useRef, useState } from 'react';

import { NonCustomerAccountTypes } from 'constants/account-types';
import { Currencies } from 'constants/currencies';
import { clearHiddenFieldValues } from 'helpers/field-config/form-page-helpers';
import { getCheckTypeIcon } from 'helpers/iconsHelper';
import {
  allowsCustomPricing,
  allowsVolumeDiscount,
  getBillPerItemLabel,
  getBillPerItemTooltip,
  getDefaultPricingSubtitle,
  getFixedPricingCustomPricingBlockedMessage,
  supportsBillPerItem,
  supportsVariableCustomPricing,
} from 'helpers/pricing-mode';
import { useForm } from 'react-hook-form';

import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import { ConfirmationModal } from 'components/ConfirmationModal/ConfirmationModal';
import { EditableSettings } from 'components/EditableSettings/EditableSettings';
import FieldConfigRenderer from 'components/FieldConfigRenderer/FieldConfigRenderer';
import {
  Button,
  FormMessage,
  Input,
  InputGroup,
  Select,
  ToggleSwitch,
} from 'components/FormComponents';
import { ToggleSwitchInput } from 'components/FormComponents/ToggleSwitchInput/ToggleSwitchInput';
import { IconButton } from 'components/IconButton/IconButton';

function isCustomPricingEnabled(value) {
  return value === true || value === 1 || value === '1';
}

function companyCheckMatchesExceptCredits(left, right) {
  if (!left || !right) {
    return false;
  }
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  for (const key of keys) {
    if (key === 'credits') {
      continue;
    }
    if (left[key] === right[key]) {
      continue;
    }
    if (
      typeof left[key] === 'object' &&
      typeof right[key] === 'object' &&
      JSON.stringify(left[key]) === JSON.stringify(right[key])
    ) {
      continue;
    }
    return false;
  }
  return true;
}

function validateCustomPriceWhenEnabled(value, useCustomPricingOn) {
  if (!useCustomPricingOn) {
    return true;
  }
  if (value === null || value === '' || (typeof value === 'string' && value.trim() === '')) {
    return 'Custom price is required';
  }
  const num = Number(value);
  if (Number.isNaN(num)) {
    return 'Custom price is required';
  }
  if (num < 0) {
    return 'Must be 0 or more';
  }
  return true;
}

function defaultVolumeDiscountPercent(companyCheck) {
  if (!isCustomPricingEnabled(companyCheck?.volume_discount_enabled)) {
    return 0;
  }
  const stored = companyCheck?.volume_discount_percent;
  if (stored != null && stored !== '') {
    return stored;
  }
  return 0;
}

function variablePriceDefaults(companyCheck) {
  const variables = companyCheck?.pricing_variables ?? [];
  const saved = companyCheck?.variable_prices ?? [];
  return variables.map(variable => {
    const match = saved.find(
      price =>
        price.price_source === variable.price_source &&
        String(price.source_value ?? '') === String(variable.source_value ?? ''),
    );
    return {
      price_source: variable.price_source,
      source_value: variable.source_value ?? '',
      price: match?.price ?? '',
    };
  });
}

function companyCheckHasCustomPricing(companyCheck) {
  const value = companyCheck?.use_custom_pricing;
  return value === true || value === 1;
}

function formatBillingPrice(price, billingCurrency) {
  if (price == null || price === '' || Number.isNaN(Number(price))) {
    return null;
  }
  const amount = Number(price).toFixed(2);
  if (!billingCurrency) {
    return amount;
  }
  return `${amount} ${String(billingCurrency).toUpperCase()}`;
}

function CustomPricingStatement({
  defaultPrice,
  customPrice,
  pricingError,
  comparisonLabel = 'the default price',
  priceLabel = null,
  compact = false,
}) {
  const defaultNum = defaultPrice != null && defaultPrice !== '' ? Number(defaultPrice) : NaN;
  const customNum = customPrice != null && customPrice !== '' ? Number(customPrice) : NaN;
  const hasPct = Number.isFinite(defaultNum) && defaultNum > 0 && Number.isFinite(customNum);
  const pct = hasPct ? ((customNum - defaultNum) / defaultNum) * 100 : null;

  if (!pricingError && !(hasPct && pct !== 0)) {
    return null;
  }

  return (
    <div
      className={
        compact
          ? 'd-flex flex-column u-margin-top--small'
          : 'd-flex flex-column u-gap-small u-margin-top--xx-small u-margin-bottom--small'
      }>
      {pricingError && (
        <span className="text-error u-font-size-x-small u-font-weight-400">* {pricingError}</span>
      )}
      {hasPct && pct !== 0 && (
        <span className="secondary-text u-font-size-x-small u-font-weight-400">
          Custom price is {Math.abs(pct).toFixed(1)}% <strong>{pct > 0 ? 'above' : 'below'}</strong>{' '}
          {priceLabel ? (
            <>
              the default price of <strong>{priceLabel}</strong>.
            </>
          ) : (
            <>{comparisonLabel}.</>
          )}
        </span>
      )}
    </div>
  );
}

export const AdminCompanyCheck = ({
  company,
  checkType,
  companyCheck,
  users,
  billingCurrency,
  disableUpdates = false,
  setLoading = () => {},
  onToggle = () => {},
  onSave = () => {},
  onAddCredits = () => {},
}) => {
  // Build initial default values from companyCheck
  const buildFormDefaults = () => {
    const configDefaults = {};
    if (checkType?.configuration) {
      checkType.configuration.forEach(config => {
        if (config.defaultValue !== undefined) {
          configDefaults[config.code] = config.defaultValue;
        }
      });
    }
    return {
      ...companyCheck,
      payment_type: companyCheck?.payment_type ?? 'prepaid_credits',
      allow_additional_charges: companyCheck?.allow_additional_charges ?? true,
      bill_per_item: companyCheck?.bill_per_item ?? false,
      use_variable_pricing: companyCheck?.use_variable_pricing ?? false,
      variable_prices: variablePriceDefaults(companyCheck),
      volume_discount_enabled: companyCheck?.volume_discount_enabled ?? false,
      volume_discount_percent: allowsVolumeDiscount(checkType?.pricing_mode)
        ? defaultVolumeDiscountPercent(companyCheck)
        : 0,
      configuration: {
        ...configDefaults,
        ...companyCheck?.configuration,
      },
    };
  };

  const {
    control,
    register,
    watch,
    reset,
    setValue,
    getValues,
    handleSubmit,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: buildFormDefaults(),
    disabled: disableUpdates,
    reValidateMode: 'onChange',
  });

  const [enabled, setEnabled] = useState(!!companyCheck);
  const [showAddCreditsModal, setShowAddCreditsModal] = useState(false);
  const [showDeductCreditsModal, setShowDeductCreditsModal] = useState(false);
  const [credits, setCredits] = useState(companyCheck?.credits);
  const [confirmPaymentType, setConfirmPaymentType] = useState(null);
  const previousPaymentType = useRef(companyCheck?.payment_type ?? 'prepaid_credits');
  const paymentTypeSnapshot = useRef(null);
  const lastSyncedCheck = useRef(companyCheck);
  const lastSyncedCheckType = useRef(checkType?.slug);

  // State for hidden field elements - updated only when field count changes to avoid loops
  const [hiddenFieldElements, setHiddenFieldElements] = useState([]);
  const hiddenFieldCountRef = useRef(0);

  // Callback for hidden fields - only update state if count changes to avoid infinite loops
  const handleHiddenFieldsRender = useCallback(elements => {
    const newCount = elements?.length || 0;
    if (newCount !== hiddenFieldCountRef.current) {
      hiddenFieldCountRef.current = newCount;
      setHiddenFieldElements(elements);
    }
  }, []);

  useEffect(() => {
    if (enabled === undefined || (enabled && companyCheck) || (!enabled && !companyCheck)) return;
    onToggle(companyCheck?.id, checkType.slug, enabled);
  }, [enabled]);

  useEffect(() => {
    setCredits(companyCheck?.credits);
    if (
      lastSyncedCheckType.current === checkType?.slug &&
      companyCheckMatchesExceptCredits(lastSyncedCheck.current, companyCheck)
    ) {
      lastSyncedCheck.current = companyCheck;
      return;
    }
    lastSyncedCheck.current = companyCheck;
    lastSyncedCheckType.current = checkType?.slug;
    setEnabled(!!companyCheck);
    previousPaymentType.current = companyCheck?.payment_type ?? 'prepaid_credits';
    reset(buildFormDefaults());
  }, [companyCheck, checkType]);

  const restorePaymentTypeSnapshot = () => {
    const snapshot = paymentTypeSnapshot.current;
    if (!snapshot) {
      previousPaymentType.current = 'prepaid_credits';
      setValue('payment_type', 'prepaid_credits');
      return;
    }
    previousPaymentType.current = snapshot.values.payment_type ?? 'prepaid_credits';
    reset(snapshot.values);
    setCredits(snapshot.credits);
  };

  const cancelPaymentTypeChange = () => {
    if (!confirmPaymentType) {
      return;
    }
    setConfirmPaymentType(null);
    restorePaymentTypeSnapshot();
  };

  const confirmPaymentTypeChange = async () => {
    const nextPaymentType = confirmPaymentType;
    setConfirmPaymentType(null);
    setCredits(-1);
    const saved = await onSave(companyCheck.id, { ...getValues(), payment_type: nextPaymentType });
    if (saved === false) {
      restorePaymentTypeSnapshot();
    }
  };

  const handleSave = handleSubmit(async () => {
    if (checkType?.configuration?.length > 0) {
      clearHiddenFieldValues(checkType.configuration, getValues, setValue, 'configuration', {
        company: company ?? null,
        companyCheckConfig: companyCheck?.configuration || null,
        candidate: null,
        candidateValues: null,
        checkDetails: null,
        subCheckDetails: null,
        selectedOptionsContext: {},
      });
    }

    const data = getValues();
    await onSave(companyCheck.id, data);
  });

  const savePaymentTypeBeforeCredits = async () => {
    if (getValues('payment_type') === companyCheck?.payment_type) {
      return true;
    }
    return (await onSave(companyCheck.id, getValues())) !== false;
  };

  const handleAddCredits = async data => {
    setShowAddCreditsModal(false);
    data['company_id'] = company.id;
    data['check_type'] = checkType.slug;
    setLoading(true);
    const ready = await savePaymentTypeBeforeCredits();
    if (!ready) {
      setLoading(false);
      return;
    }
    const { data: credits } = await onAddCredits(data);
    setCredits(credits);
    setLoading(false);
  };

  const handleDeductCredits = async data => {
    setLoading(true);
    data = {
      ...data,
      deduction: true,
      units: data.units,
      company_id: company.id,
      check_type: checkType.slug,
    };
    setShowDeductCreditsModal(false);
    const ready = await savePaymentTypeBeforeCredits();
    if (!ready) {
      setLoading(false);
      return;
    }
    const { data: credits } = await onAddCredits(data);
    setCredits(credits);
    setLoading(false);
  };

  const companyCheckConfig = companyCheck?.configuration || null;
  const pricingMode = checkType?.pricing_mode;
  const isFixedPricingMode = !allowsCustomPricing(pricingMode);
  const hasLegacyCustomPricing = isFixedPricingMode && companyCheckHasCustomPricing(companyCheck);
  const canEditCustomPricing = allowsCustomPricing(pricingMode) || hasLegacyCustomPricing;
  const supportsVolumeDiscount =
    companyCheck?.supports_volume_discount ?? allowsVolumeDiscount(pricingMode);
  const isBillPerItemSupported =
    companyCheck?.supports_bill_per_item ??
    supportsBillPerItem(pricingMode, checkType?.has_subchecks);
  const nonCustomer = NonCustomerAccountTypes.includes(company?.account_type);
  const paymentType = watch('payment_type');
  const variablePricingValue = watch('use_variable_pricing');
  const variablePricingBeforeClear = useRef(variablePricingValue);
  variablePricingBeforeClear.current = variablePricingValue;
  const showCredits = !nonCustomer && paymentType === 'prepaid_credits';
  const showPricing = !nonCustomer && paymentType !== 'subscription';
  const useCustomPricingOn = isCustomPricingEnabled(watch('use_custom_pricing'));
  const pricingVariables = companyCheck?.pricing_variables ?? [];
  const isOnAccount = paymentType === 'on_account';
  const showVariablePricing =
    isOnAccount &&
    useCustomPricingOn &&
    supportsVariableCustomPricing(pricingMode) &&
    pricingVariables.length > 0;
  const useVariablePricingOn =
    showVariablePricing && isCustomPricingEnabled(watch('use_variable_pricing'));
  const billPerItemOn = isCustomPricingEnabled(watch('bill_per_item'));
  const volumeDiscountEnabledOn = isCustomPricingEnabled(watch('volume_discount_enabled'));
  const pricingError = useCustomPricingOn ? errors?.pricing?.message : undefined;
  const showVolumeDiscountToggle =
    useCustomPricingOn && supportsVolumeDiscount && (!isBillPerItemSupported || billPerItemOn);
  const showVolumeDiscountField = showVolumeDiscountToggle && volumeDiscountEnabledOn;

  useEffect(() => {
    if (!isOnAccount) {
      setValue('use_variable_pricing', false);
    }
  }, [isOnAccount, setValue]);

  useEffect(() => {
    const previous = previousPaymentType.current;
    if (!companyCheck?.id || previous === paymentType) {
      return;
    }
    previousPaymentType.current = paymentType;
    const snapshot = {
      values: {
        ...getValues(),
        payment_type: previous,
        use_variable_pricing: variablePricingBeforeClear.current,
      },
      credits,
    };
    paymentTypeSnapshot.current = snapshot;
    if (
      previous === 'prepaid_credits' &&
      ['on_account', 'subscription'].includes(paymentType) &&
      Number(credits) > 0
    ) {
      setConfirmPaymentType(paymentType);
      return;
    }

    const values = { ...getValues(), payment_type: paymentType };
    if (paymentType === 'prepaid_credits') {
      if (Number(credits) === -1) {
        values.credits = 0;
        setCredits(0);
        setValue('credits', 0);
      }
    } else if (paymentType === 'on_account' || paymentType === 'subscription') {
      values.credits = -1;
      setCredits(-1);
      setValue('credits', -1);
    }
    if (paymentType === 'subscription') {
      values.use_custom_pricing = false;
      values.use_variable_pricing = false;
      values.pricing = 0;
      values.bill_per_item = false;
      values.volume_discount_enabled = false;
      values.volume_discount_percent = 0;
      setValue('use_custom_pricing', false);
      setValue('use_variable_pricing', false);
      setValue('pricing', 0);
      setValue('bill_per_item', false);
      setValue('volume_discount_enabled', false);
      setValue('volume_discount_percent', 0);
    }

    onSave(companyCheck.id, values).then(saved => {
      if (saved === false) {
        restorePaymentTypeSnapshot();
      }
    });
  }, [paymentType]);

  useEffect(() => {
    if (!nonCustomer) {
      if (getValues('payment_type') === 'no_charge') {
        setValue('payment_type', 'prepaid_credits');
      }
      return;
    }
    setValue('payment_type', 'no_charge');
    setValue('use_custom_pricing', false);
    setValue('use_variable_pricing', false);
    setValue('pricing', 0);
    setValue('bill_per_item', false);
    setValue('volume_discount_enabled', false);
    setValue('volume_discount_percent', 0);
  }, [nonCustomer, getValues, setValue]);

  useEffect(() => {
    if (!useCustomPricingOn) {
      clearErrors('pricing');
      setValue('pricing', 0);
      setValue('use_variable_pricing', false);
      setValue('volume_discount_enabled', false);
      if (supportsVolumeDiscount) {
        setValue('volume_discount_percent', 0);
      }
      return;
    }
    if (!supportsVolumeDiscount) {
      return;
    }
    if (isBillPerItemSupported && !billPerItemOn) {
      setValue('volume_discount_enabled', false);
      setValue('volume_discount_percent', 0);
      return;
    }
    if (!volumeDiscountEnabledOn) {
      setValue('volume_discount_percent', 0);
      return;
    }
    if (!showVolumeDiscountField) {
      return;
    }
    const current = getValues('volume_discount_percent');
    if (current === null || current === '' || Number.isNaN(Number(current))) {
      setValue('volume_discount_percent', 0);
    }
  }, [
    useCustomPricingOn,
    showVolumeDiscountField,
    volumeDiscountEnabledOn,
    billPerItemOn,
    isBillPerItemSupported,
    supportsVolumeDiscount,
    clearErrors,
    getValues,
    setValue,
  ]);

  const defaultPricingSubtitle = getDefaultPricingSubtitle(
    billingCurrency,
    companyCheck?.standard_price ?? null,
    pricingMode,
  );
  const candidate = null;
  const checkDetails = null;
  const subCheckDetails = null;
  const candidateValues = null;

  return (
    <ToggleSwitchInput
      name={checkType.slug}
      className="u-margin-right--large"
      text={`Activate ${checkType.title}`}
      subtext={`(${checkType.slug})`}
      toggle={!disableUpdates}
      icon={getCheckTypeIcon(checkType.slug)}
      value={enabled}
      onChange={e => setEnabled(e.target.checked)}>
      <ConfirmationModal
        visible={!!confirmPaymentType}
        title="Credits will be lost"
        subtitle={`${credits} prepaid credits will be removed when this check is switched to ${confirmPaymentType === 'subscription' ? 'subscription' : 'on account'}.`}
        buttonTitle="Continue"
        onClose={cancelPaymentTypeChange}
        onConfirmation={confirmPaymentTypeChange}
      />
      <AddCreditsModal
        visible={showAddCreditsModal}
        billingCurrency={billingCurrency}
        users={users}
        checkType={checkType}
        companyCheck={companyCheck}
        onAdd={handleAddCredits}
        onClose={() => setShowAddCreditsModal(false)}
      />
      <DeductCreditsModal
        visible={showDeductCreditsModal}
        checkType={checkType}
        billingCurrency={billingCurrency}
        companyCheck={companyCheck}
        onDeduct={handleDeductCredits}
        onClose={() => setShowDeductCreditsModal(false)}
      />

      {showCredits && (
        <>
          <h3 className="t-tiny u-uppercase">credits</h3>
          <div className="d-flex justify-content-between">
            <h1 className="u-margin-top--0">{credits}</h1>
            {!disableUpdates && (
              <div>
                <IconButton
                  icon="minus"
                  tip="Deduct credits"
                  disabled={Number(credits) < 1}
                  onClick={() => setShowDeductCreditsModal(true)}
                  className="u-margin-right--small"
                />
                <IconButton
                  icon="plus"
                  tip="Add credits"
                  onClick={() => setShowAddCreditsModal(true)}
                />
              </div>
            )}
          </div>
        </>
      )}

      <form onSubmit={handleSave}>
        {!nonCustomer && (
          <InputGroup title="Payment type">
            <Select name="payment_type" register={register} value={paymentType} useDefault>
              <Select.Item value="on_account">On account</Select.Item>
              <Select.Item value="prepaid_credits">Prepaid credits</Select.Item>
              <Select.Item value="subscription">Subscription</Select.Item>
            </Select>
          </InputGroup>
        )}
        {showPricing && (
          <EditableSettings
            title="Custom pricing"
            subtitle={defaultPricingSubtitle}
            open={canEditCustomPricing ? useCustomPricingOn : true}
            toggleable={canEditCustomPricing}
            hideCollapseChevron={!useCustomPricingOn}
            register={register}
            name="use_custom_pricing"
            className="u-margin-top--large u-margin-bottom">
            {canEditCustomPricing ? (
              <>
                {useCustomPricingOn ? (
                  <>
                    {showVariablePricing && (
                      <ToggleSwitch
                        register={register}
                        name="use_variable_pricing"
                        label="Variable pricing"
                        tooltip={
                          pricingMode === 'subcheck_variable'
                            ? 'When off, one custom price is charged for every subcheck. When on, each subcheck uses the custom price for its variable.'
                            : 'When off, one custom price is charged for the check. When on, the check uses the custom price for the variable that applies.'
                        }
                      />
                    )}
                    {!useVariablePricingOn && (
                      <>
                        <InputGroup
                          noMargin
                          title={
                            billingCurrency
                              ? `Custom price (${String(billingCurrency).toUpperCase()}) *`
                              : 'Custom price *'
                          }
                          description={billingCurrency ? "Company's billing currency." : undefined}>
                          <Input
                            register={register}
                            name="pricing"
                            type="number"
                            error={pricingError}
                            noErrorMessage
                            validators={{
                              validate: value =>
                                validateCustomPriceWhenEnabled(
                                  value,
                                  isCustomPricingEnabled(getValues('use_custom_pricing')),
                                ),
                            }}
                            inputProps={{ step: '0.01', min: '0' }}
                          />
                        </InputGroup>
                        <CustomPricingStatement
                          defaultPrice={companyCheck?.standard_price ?? null}
                          customPrice={watch('pricing')}
                          pricingError={pricingError}
                          priceLabel={formatBillingPrice(
                            companyCheck?.standard_price,
                            billingCurrency,
                          )}
                        />
                      </>
                    )}
                    {useVariablePricingOn &&
                      pricingVariables.map((variable, index) => {
                        const standardPrice =
                          variable.standard_price != null && variable.standard_price !== ''
                            ? Number(variable.standard_price)
                            : null;
                        const variableName =
                          variable.price_name || variable.source_value || variable.price_source;
                        const defaultPriceLabel = formatBillingPrice(
                          standardPrice,
                          billingCurrency,
                        );
                        return (
                          <div
                            key={`${variable.price_source}:${variable.source_value}`}
                            style={{ marginBottom: 24 }}>
                            <InputGroup
                              noMargin
                              title={variableName}
                              description={
                                defaultPriceLabel ? (
                                  <>
                                    The default price for <strong>{variableName}</strong> is{' '}
                                    {defaultPriceLabel}.
                                  </>
                                ) : undefined
                              }>
                              <input
                                type="hidden"
                                {...register(`variable_prices.${index}.price_source`)}
                              />
                              <input
                                type="hidden"
                                {...register(`variable_prices.${index}.source_value`)}
                              />
                              <Input
                                register={register}
                                name={`variable_prices.${index}.price`}
                                type="number"
                                inputProps={{ step: '0.01', min: '0' }}
                              />
                              <CustomPricingStatement
                                defaultPrice={standardPrice}
                                customPrice={watch(`variable_prices.${index}.price`)}
                                compact
                              />
                            </InputGroup>
                          </div>
                        );
                      })}
                    {isBillPerItemSupported && (
                      <ToggleSwitch
                        register={register}
                        name="bill_per_item"
                        label={getBillPerItemLabel(checkType?.slug)}
                        tooltip={getBillPerItemTooltip(checkType?.slug)}
                      />
                    )}
                    {showVolumeDiscountToggle && (
                      <ToggleSwitch
                        register={register}
                        name="volume_discount_enabled"
                        label={
                          isBillPerItemSupported
                            ? 'Volume discount on subsequent items'
                            : 'Volume discount on subsequent subchecks'
                        }
                        tooltip="When on, set a percent discount off the custom price for each additional subcheck or item after the first."
                      />
                    )}
                    {showVolumeDiscountField && (
                      <InputGroup title="Volume discount (% off)">
                        <Input
                          register={register}
                          name="volume_discount_percent"
                          type="number"
                          inputProps={{ step: '0.01', min: '0', max: '100' }}
                        />
                        <FormMessage
                          message="Percent off the custom price for each additional subcheck or item after the first (0%–100%). E.g. 30 means additional subchecks or items are charged at 70% of the custom price."
                          type="Info"
                          className="u-margin-top--small"
                          closeable={false}
                        />
                      </InputGroup>
                    )}
                  </>
                ) : null}
                {hasLegacyCustomPricing && (
                  <FormMessage
                    message="This check type has fixed pricing. Legacy custom pricing is retained while you transition. If you turn custom pricing off, the standard pricing will apply and will not be editable."
                    type="Info"
                    noMargin
                    closeable={false}
                  />
                )}
              </>
            ) : (
              <FormMessage
                message={getFixedPricingCustomPricingBlockedMessage(pricingMode)}
                type="Info"
                noMargin
                closeable={false}
              />
            )}
          </EditableSettings>
        )}

        <ToggleSwitch
          register={register}
          name="has_sensitive_results"
          label="May contain sensitive information"
        />

        <ToggleSwitch
          register={register}
          name="archived"
          tooltip="Prevent check from being initiated. Client will still be able to view launched checks."
          label="Archived"
        />

        {/* Hidden fields: register with form outside ToggleSwitchInput to avoid triggering Collapse */}
        {hiddenFieldElements}

        <FieldConfigRenderer
          fields={checkType?.configuration}
          control={control}
          register={register}
          setValue={setValue}
          getValues={getValues}
          watch={watch}
          formValuesPrefix="configuration"
          errors={errors}
          company={company ?? null}
          companyCheckConfig={companyCheckConfig}
          candidate={candidate}
          checkDetails={checkDetails}
          subCheckDetails={subCheckDetails}
          candidateValues={candidateValues}
          onHiddenFieldsRender={handleHiddenFieldsRender}
          enforceFieldValidation={false}
        />

        {!disableUpdates && (
          <Button submit className="u-width-100 u-margin-top--large">
            Save
          </Button>
        )}
      </form>
    </ToggleSwitchInput>
  );
};

const AddCreditsModal = ({
  visible,
  users,
  checkType,
  billingCurrency,
  companyCheck,
  onAdd = () => {},
  onClose = () => {},
}) => {
  const { register, watch, handleSubmit, reset } = useForm({
    defaultValues: {
      currency: billingCurrency,
      unit_price: companyCheck?.use_custom_pricing
        ? companyCheck.pricing
        : (companyCheck?.standard_price ?? 0),
      units: 0,
    },
  });

  useEffect(() => {
    reset({
      currency: billingCurrency,
      unit_price: companyCheck?.use_custom_pricing
        ? companyCheck.pricing
        : (companyCheck?.standard_price ?? 0),
      units: 0,
    });
  }, [companyCheck, billingCurrency]);

  return (
    <AnimatedModal
      title={`Add ${checkType.title} credits`}
      visible={visible}
      onClose={onClose}
      small>
      <form className="u-margin-top" onSubmit={handleSubmit(onAdd)}>
        <InputGroup title="Required by">
          <Select name="required_by" register={register} useDefault value={watch('required_by')}>
            {users.map(user => (
              <Select.Item key={user.id} value={user.name}>
                {user.name} <span className="t-tiny"> ({user.email})</span>
              </Select.Item>
            ))}
          </Select>
        </InputGroup>
        <InputGroup title="Currency">
          <Select name="currency" register={register} value={watch('currency')} useDefault>
            {Object.keys(Currencies).map(currency => (
              <Select.Item key={currency} value={currency}>
                {currency.toUpperCase()}
              </Select.Item>
            ))}
          </Select>
        </InputGroup>
        <InputGroup title="Unit price">
          <Input
            name="unit_price"
            type="number"
            register={register}
            inputProps={{ step: '0.01' }}
          />
        </InputGroup>
        <InputGroup title="Units" useDefault>
          <Input
            name="units"
            type="number"
            register={register}
            validators={{ required: true, min: 1 }}
            inputProps={{ min: 1, step: 1 }}
          />
        </InputGroup>
        <Button submit className="u-width-100 u-margin-top--large">
          Add
        </Button>
      </form>
    </AnimatedModal>
  );
};

const DeductCreditsModal = ({
  visible,
  checkType,
  billingCurrency,
  companyCheck,
  onDeduct = () => {},
  onClose = () => {},
}) => {
  const { register, watch, handleSubmit, reset } = useForm({
    defaultValues: {
      currency: billingCurrency,
      unit_price: companyCheck?.use_custom_pricing
        ? companyCheck.pricing
        : (companyCheck?.standard_price ?? 0),
      units: companyCheck?.credits,
    },
  });

  useEffect(() => {
    reset({
      currency: billingCurrency,
      unit_price: companyCheck?.use_custom_pricing
        ? companyCheck.pricing
        : (companyCheck?.standard_price ?? 0),
      units: companyCheck?.credits,
    });
  }, [companyCheck, billingCurrency]);

  return (
    <AnimatedModal
      title={`Deduct ${checkType.title} credits`}
      visible={visible}
      onClose={onClose}
      small>
      <form className="u-margin-top" onSubmit={handleSubmit(onDeduct)}>
        <InputGroup title="Currency">
          <Select name="currency" register={register} value={watch('currency')} useDefault>
            {Object.keys(Currencies).map(currency => (
              <Select.Item key={currency} value={currency}>
                {currency.toUpperCase()}
              </Select.Item>
            ))}
          </Select>
        </InputGroup>
        <InputGroup title="Unit price">
          <Input
            name="unit_price"
            type="number"
            register={register}
            inputProps={{ step: '0.01' }}
          />
        </InputGroup>
        <InputGroup title="Units" useDefault>
          <Input
            name="units"
            type="number"
            register={register}
            validators={{
              required: true,
              validate: value => {
                const units = Number(value);
                return units >= 1 && units <= Number(companyCheck?.credits);
              },
            }}
            inputProps={{ min: 1, step: 1, max: companyCheck?.credits }}
          />
        </InputGroup>
        <Button type="delete" submit className="u-width-100 u-margin-top--large">
          Deduct
        </Button>
      </form>
    </AnimatedModal>
  );
};
